import { Fragment, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { type AutosaveState, AutosaveStatus } from '@components/AutosaveStatus';
import { Box, Button } from '@mui/material';

import { getIntakeFieldFormatError } from '../../fieldFormatValidation';
import {
  defaultFieldValue,
  requiresAffirmativeAcknowledgement,
  sameAsPracticeParentKey,
} from '../../fieldPersistence';
import { type IntakeRepeatRecord, repeatGroupKey } from '../../repeatableFields';
import type {
  GeneratedIntakeField,
  GeneratedIntakeGroupHelp,
  IntakeFieldSuggestion,
  IntakeFieldValue,
} from '../../types';

import { GroupBlurb, GroupHelpTrigger, HelpPanelDrawer } from './HelpPanelDrawer';
import { type IntakeFieldSaveState, IntakePageFieldInput } from './IntakePageFieldInput';
import { IntakePageFooter } from './IntakePageFooter';
import { IntakePageHeader } from './IntakePageHeader';
import { RepeatableIntakeGroup } from './RepeatableIntakeGroup';
import { cardSx, groupTitleSx, wf } from './tokens';
import { useGroupHelpPanel } from './useGroupHelpPanel';

const EMPTY_GROUPS: GeneratedIntakeGroupHelp[] = [];
const EMPTY_SUGGESTIONS: Record<string, IntakeFieldSuggestion> = {};
const EMPTY_REASONS: Record<string, string> = {};
const EMPTY_MASKED_VALUES: Record<string, string> = {};
const EMPTY_ACKNOWLEDGED_VALUES: Record<string, boolean> = {};
const NO_HELP_ACTION = () => undefined;

interface GroupSaveState {
  status: AutosaveState;
  isDirty: boolean;
  lastSavedAt: Date | null;
  retries: Array<() => void>;
}

function aggregateSaveStates(states: IntakeFieldSaveState[]): GroupSaveState {
  const failed = states.filter(state => state.status === 'error');
  const isDirty = states.some(state => state.isDirty);
  const status: AutosaveState = failed.length
    ? 'error'
    : states.some(state => state.status === 'saving')
      ? 'saving'
      : states.some(state => state.status === 'saved') && !isDirty
        ? 'saved'
        : 'idle';
  const savedTimes = states.flatMap(state => (state.lastSavedAt ? [state.lastSavedAt] : []));

  return {
    status,
    isDirty,
    lastSavedAt:
      savedTimes.length > 0
        ? new Date(Math.max(...savedTimes.map(savedAt => savedAt.getTime())))
        : null,
    retries: failed.map(state => state.retry),
  };
}

function groupSaveState(
  fields: GeneratedIntakeField[],
  fieldSaveStates: Record<string, IntakeFieldSaveState>,
  draftValues: Record<string, IntakeFieldValue>,
  acknowledgedValues: Record<string, IntakeFieldValue>,
): GroupSaveState {
  const states = fields.map(field => {
    const key = field.field_id || field.field_key;
    return (
      fieldSaveStates[key] ?? {
        status: 'idle',
        isDirty: JSON.stringify(draftValues[key]) !== JSON.stringify(acknowledgedValues[key]),
        lastSavedAt: null,
        retry: () => undefined,
      }
    );
  });
  return aggregateSaveStates(states);
}

function addressSectionHeading(field: GeneratedIntakeField): string | null {
  if (field.destination.kind !== 'intake_item_evidence') return null;
  const keys = [field.parent_field_key, field.destination.field_key, field.field_key].filter(
    Boolean,
  );
  if (keys.some(key => key.startsWith('billing_address'))) return 'Billing Address';
  if (keys.some(key => key.startsWith('mailing_address'))) return 'Mailing Address';
  return null;
}

interface IntakeFieldsPageProps {
  title: string;
  fields: GeneratedIntakeField[];
  groups?: GeneratedIntakeGroupHelp[];
  suggestions?: Record<string, IntakeFieldSuggestion>;
  onAcceptSuggestion?: (suggestion: IntakeFieldSuggestion) => Promise<void>;
  onDiscardSuggestion?: (suggestion: IntakeFieldSuggestion) => void;
  onContinue: () => void;
  onBack?: () => void;
  nextLabel: string;
  draftValues: Record<string, IntakeFieldValue>;
  acknowledgedValues: Record<string, IntakeFieldValue>;
  hasAcknowledgedValues?: Record<string, boolean>;
  onDraftChange: (field: GeneratedIntakeField, value: IntakeFieldValue) => void;
  onPersistField: (field: GeneratedIntakeField, value: IntakeFieldValue) => Promise<void>;
  onHelpAction?: (action: 'message_specialist') => void;
  disabledReasons?: Record<string, string>;
  readOnlySensitiveFields?: boolean;
  /** field_id -> server-masked display form of a secure value already on file. */
  maskedSensitiveValues?: Record<string, string>;
  saveMode?: 'autosave' | 'explicit';
  isSaving?: boolean;
  saveErrorCount?: number;
  onSaveChanges?: () => void;
  embedded?: boolean;
  showRequiredErrors?: boolean;
  validationRequested?: boolean;
  repeatRecordsByGroup?: Record<string, IntakeRepeatRecord[]>;
  /** Suggestion entry key (see `suggestionEntryKey`) to the document that produced it. */
  suggestionDocumentReferenceIds?: Record<string, string>;
  /** Document reference id to the name shown for that document. */
  documentNameByReferenceId?: Record<string, string>;
  onPersistRepeatGroup?: (
    fields: GeneratedIntakeField[],
    records: IntakeRepeatRecord[],
  ) => Promise<void>;
}

function hasFieldValue(value: IntakeFieldValue): boolean {
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'string') return value.trim().length > 0;
  return value !== null && value !== undefined;
}

/**
 * True when a field must be skipped by the required-field Next gate and its
 * highlight — read-only/unavailable fields the user cannot act on, and
 * repeat-scoped fields, whose values live in repeatRecordsByGroup (not
 * draftValues) and are validated per-record inside RepeatableIntakeGroup.
 *
 * Shared by the Continue-blocking check and the per-field highlight so the
 * two can't drift: a field excluded here must never render
 * "<label> is required." while Continue isn't actually blocked by it.
 */
function isExemptFromRequiredGate(
  field: GeneratedIntakeField,
  key: string,
  disabledReasons: Record<string, string>,
): boolean {
  if (field.is_writable === false) return true;
  if (disabledReasons[key]) return true;
  if (field.repeat_scope && field.repeat_scope !== 'none') return true;
  return false;
}

/** True when a required field's current value doesn't satisfy the requirement. */
function fieldMissingRequiredValue(field: GeneratedIntakeField, value: IntakeFieldValue): boolean {
  if (!field.is_required) return false;
  return !hasFieldValue(value) || (requiresAffirmativeAcknowledgement(field) && value !== true);
}

/**
 * The generic entry page. Fields come from the generator, so this component is
 * correct for any generated page without being written per page. Exact-ID document
 * suggestions share the same durable typed-entry and persistence controls.
 */
export function IntakeFieldsPage({
  title,
  fields,
  groups: generatedGroups = EMPTY_GROUPS,
  suggestions = EMPTY_SUGGESTIONS,
  onAcceptSuggestion,
  onDiscardSuggestion,
  onContinue,
  onBack,
  nextLabel,
  draftValues,
  acknowledgedValues,
  hasAcknowledgedValues = EMPTY_ACKNOWLEDGED_VALUES,
  onDraftChange,
  onPersistField,
  onHelpAction = NO_HELP_ACTION,
  disabledReasons = EMPTY_REASONS,
  readOnlySensitiveFields = false,
  maskedSensitiveValues = EMPTY_MASKED_VALUES,
  saveMode = 'autosave',
  isSaving = false,
  saveErrorCount = 0,
  onSaveChanges,
  embedded = false,
  showRequiredErrors = false,
  validationRequested = false,
  repeatRecordsByGroup = {},
  suggestionDocumentReferenceIds = EMPTY_REASONS,
  documentNameByReferenceId = EMPTY_REASONS,
  onPersistRepeatGroup,
}: IntakeFieldsPageProps) {
  const [validationAttempted, setValidationAttempted] = useState(false);
  const [fieldSaveStates, setFieldSaveStates] = useState<Record<string, IntakeFieldSaveState>>({});
  const onDraftChangeRef = useRef(onDraftChange);
  const onPersistFieldRef = useRef(onPersistField);
  const onAcceptSuggestionRef = useRef(onAcceptSuggestion);
  const onDiscardSuggestionRef = useRef(onDiscardSuggestion);
  onDraftChangeRef.current = onDraftChange;
  onPersistFieldRef.current = onPersistField;
  onAcceptSuggestionRef.current = onAcceptSuggestion;
  onDiscardSuggestionRef.current = onDiscardSuggestion;
  useEffect(() => setValidationAttempted(false), [title]);
  const handleFieldDraftChange = useCallback(
    (field: GeneratedIntakeField, value: IntakeFieldValue) =>
      onDraftChangeRef.current(field, value),
    [],
  );
  const handleFieldPersist = useCallback(
    (field: GeneratedIntakeField, value: IntakeFieldValue) =>
      onPersistFieldRef.current(field, value),
    [],
  );
  const handleAcceptSuggestion = useCallback(
    (suggestion: IntakeFieldSuggestion) =>
      onAcceptSuggestionRef.current?.(suggestion) ?? Promise.resolve(),
    [],
  );
  const handleDiscardSuggestion = useCallback(
    (suggestion: IntakeFieldSuggestion) => onDiscardSuggestionRef.current?.(suggestion),
    [],
  );
  const handleFieldSaveStateChange = useCallback(
    (field: GeneratedIntakeField, state: IntakeFieldSaveState) => {
      const key = field.field_id || field.field_key;
      setFieldSaveStates(current =>
        current[key] === state ? current : { ...current, [key]: state },
      );
    },
    [],
  );
  const handleNamedSaveStateChange = useCallback((key: string, state: IntakeFieldSaveState) => {
    setFieldSaveStates(current =>
      current[key] === state ? current : { ...current, [key]: state },
    );
  }, []);
  const nonFileFields = useMemo(
    () => fields.filter(field => field.data_type.toLowerCase() !== 'file'),
    [fields],
  );
  const visibleFields = useMemo(() => {
    const derivedAddressParentKeys = new Set(
      nonFileFields.flatMap(field => {
        const parentKey = sameAsPracticeParentKey(field);
        const key = field.field_id || field.field_key;
        const value = draftValues[key] ?? acknowledgedValues[key] ?? defaultFieldValue(field);
        return parentKey && value === true ? [parentKey] : [];
      }),
    );
    return nonFileFields.filter(field => !derivedAddressParentKeys.has(field.parent_field_key));
  }, [acknowledgedValues, draftValues, nonFileFields]);
  const fieldsByGroup = useMemo(
    () =>
      visibleFields.reduce<Record<string, GeneratedIntakeField[]>>((acc, field) => {
        const groupId = field.group_id || field.group_name || 'details';
        (acc[groupId] ??= []).push(field);
        return acc;
      }, {}),
    [visibleFields],
  );
  const { selectedGroup, selectedGroupId, openGroup, closeGroup } =
    useGroupHelpPanel(generatedGroups);
  const drawerId = `intake-field-group-help-${useId().replace(/:/g, '')}`;

  const dirtyFieldCount = visibleFields.filter(field => {
    const key = field.field_id || field.field_key;
    return JSON.stringify(draftValues[key]) !== JSON.stringify(acknowledgedValues[key]);
  }).length;
  const validationErrors = useMemo(
    () =>
      Object.fromEntries(
        visibleFields.flatMap(field => {
          const key = field.field_id || field.field_key;
          if (field.is_writable === false || disabledReasons[key]) return [];
          const value = draftValues[key] ?? acknowledgedValues[key] ?? defaultFieldValue(field);
          const error = getIntakeFieldFormatError(field, value, acknowledgedValues[key]);
          return error ? [[key, error]] : [];
        }),
      ),
    [acknowledgedValues, disabledReasons, draftValues, visibleFields],
  );
  const hasValidationErrors = Object.keys(validationErrors).length > 0;
  const shouldShowValidation = validationAttempted || validationRequested;
  const missingRequiredFields = useMemo(
    () =>
      visibleFields.filter(field => {
        const key = field.field_id || field.field_key;
        if (isExemptFromRequiredGate(field, key, disabledReasons)) return false;
        const value = draftValues[key] ?? acknowledgedValues[key] ?? defaultFieldValue(field);
        return fieldMissingRequiredValue(field, value);
      }),
    [acknowledgedValues, disabledReasons, draftValues, visibleFields],
  );
  const hasMissingRequiredFields = missingRequiredFields.length > 0;
  const missingAffirmativeAcknowledgement = visibleFields.find(field => {
    if (!requiresAffirmativeAcknowledgement(field)) return false;
    const key = field.field_id || field.field_key;
    const draftValue = draftValues[key] ?? acknowledgedValues[key];
    return draftValue !== true || acknowledgedValues[key] !== true;
  });
  // Only the mailing-address publication attestation gates Continue — how
  // complete the page is never does, so a provider can move around the wizard
  // and fill in what they can. Missing required fields are still highlighted
  // inline and called out below, and Review holds the actual submission.
  const continueDisabledReason = missingAffirmativeAcknowledgement
    ? 'Acknowledge that the mailing address will be published before continuing.'
    : undefined;
  const missingRequiredNote =
    shouldShowValidation && hasMissingRequiredFields
      ? 'Some required fields are still empty. You can come back to them later.'
      : undefined;
  const validateThenContinue = () => {
    setValidationAttempted(true);
    if (!missingAffirmativeAcknowledgement) onContinue();
  };
  const validateThenSave = () => {
    setValidationAttempted(true);
    if (!hasValidationErrors) onSaveChanges?.();
  };

  return (
    <>
      {!embedded && (
        <IntakePageHeader
          title={title}
          sub={
            fields.length === 0
              ? 'Nothing on this page is needed for your setup.'
              : 'Fill in the details below. Each field saves as you go.'
          }
        />
      )}

      {Object.entries(fieldsByGroup).map(([groupId, groupFields]) => {
        const generatedGroup = generatedGroups.find(group => group.group_id === groupId);
        const groupName = generatedGroup?.group_name || groupFields[0]?.group_name || 'Details';
        const isRepeated = groupFields.some(
          field => field.repeat_scope && field.repeat_scope !== 'none',
        );
        const repeatedStatePrefix = `${groupId}:`;
        const saveState = isRepeated
          ? aggregateSaveStates(
              Object.entries(fieldSaveStates)
                .filter(([key]) => key.startsWith(repeatedStatePrefix))
                .map(([, state]) => state),
            )
          : groupSaveState(groupFields, fieldSaveStates, draftValues, acknowledgedValues);
        return (
          <Box key={groupId} sx={cardSx}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '8px',
                marginBottom: '10px',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                <Box sx={groupTitleSx}>{groupName}</Box>
                {generatedGroup?.help_panel && (
                  <GroupHelpTrigger
                    groupName={groupName}
                    controlsId={drawerId}
                    expanded={selectedGroupId === groupId}
                    onOpen={opener => openGroup(groupId, opener)}
                  />
                )}
              </Box>
              <Box
                data-intake-group-save-indicator
                sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, gap: 0.5 }}
              >
                <Box
                  role="status"
                  aria-live="polite"
                  aria-atomic="true"
                  aria-label={`${groupName} save status`}
                >
                  <AutosaveStatus
                    status={saveState.status}
                    isDirty={saveState.isDirty}
                    lastSavedAt={saveState.lastSavedAt}
                    messages={{ error: 'Could not save changes' }}
                    verbose
                  />
                </Box>
                {saveState.retries.length > 0 && (
                  <Button
                    size="small"
                    onClick={() => saveState.retries.forEach(retry => retry())}
                    aria-label={`Retry ${groupName} save`}
                  >
                    Retry save
                  </Button>
                )}
              </Box>
            </Box>
            <GroupBlurb blurb={generatedGroup?.blurb} sx={{ mt: '-8px', mb: '16px' }} />
            {isRepeated && onPersistRepeatGroup ? (
              <RepeatableIntakeGroup
                fields={groupFields}
                records={repeatRecordsByGroup[repeatGroupKey(groupFields[0])] ?? []}
                suggestions={suggestions}
                suggestionDocumentReferenceIds={suggestionDocumentReferenceIds}
                documentNameByReferenceId={documentNameByReferenceId}
                onPersist={records => onPersistRepeatGroup(groupFields, records)}
                onAcceptSuggestion={onAcceptSuggestion ? handleAcceptSuggestion : undefined}
                onDiscardSuggestion={onDiscardSuggestion ? handleDiscardSuggestion : undefined}
                showRequiredErrors={showRequiredErrors}
                saveStateKeyPrefix={repeatedStatePrefix}
                onSaveStateChange={handleNamedSaveStateChange}
              />
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                  gap: '10px',
                }}
              >
                {groupFields.map((field, index) => {
                  const key = field.field_id || field.field_key;
                  const sectionHeading = addressSectionHeading(field);
                  const fieldLayout = sameAsPracticeParentKey(field) ? 'full-width' : 'grid-cell';
                  const previousSectionHeading =
                    index > 0 ? addressSectionHeading(groupFields[index - 1]) : null;
                  const acknowledgedValue =
                    acknowledgedValues[key] ?? (field.data_type === 'boolean' ? false : '');
                  const draftValue = draftValues[key] ?? acknowledgedValue;
                  const requiredValueMissing =
                    (showRequiredErrors || shouldShowValidation) &&
                    !isExemptFromRequiredGate(field, key, disabledReasons) &&
                    fieldMissingRequiredValue(field, draftValue);
                  return (
                    <Fragment key={key}>
                      {sectionHeading && sectionHeading !== previousSectionHeading && (
                        <Box
                          component="h3"
                          sx={{
                            gridColumn: '1 / -1',
                            color: wf.ink,
                            fontSize: '12px',
                            fontWeight: 700,
                            letterSpacing: '0.02em',
                            margin: '4px 0 0',
                          }}
                        >
                          {sectionHeading}
                        </Box>
                      )}
                      <Box
                        data-intake-field-layout={fieldLayout}
                        sx={fieldLayout === 'full-width' ? { gridColumn: '1 / -1' } : undefined}
                      >
                        <IntakePageFieldInput
                          field={field}
                          draftValue={draftValue}
                          acknowledgedValue={acknowledgedValue}
                          hasAcknowledgedValue={hasAcknowledgedValues[key] ?? false}
                          onDraftChange={handleFieldDraftChange}
                          onPersist={handleFieldPersist}
                          onAcceptSuggestion={
                            onAcceptSuggestion ? handleAcceptSuggestion : undefined
                          }
                          onDiscardSuggestion={
                            onDiscardSuggestion ? handleDiscardSuggestion : undefined
                          }
                          suggestion={
                            suggestions[field.field_id]?.field_id === field.field_id
                              ? suggestions[field.field_id]
                              : undefined
                          }
                          readOnly={
                            readOnlySensitiveFields &&
                            field.destination.kind === 'intake_item_sensitive'
                          }
                          disabled={Boolean(disabledReasons[key])}
                          disabledReason={disabledReasons[key]}
                          maskedValue={maskedSensitiveValues[field.field_id]}
                          saveMode={saveMode}
                          showStatus={false}
                          onSaveStateChange={handleFieldSaveStateChange}
                          validationError={
                            requiredValueMissing
                              ? `${field.label} is required.`
                              : shouldShowValidation
                                ? validationErrors[key]
                                : undefined
                          }
                        />
                      </Box>
                    </Fragment>
                  );
                })}
              </Box>
            )}
          </Box>
        );
      })}

      {visibleFields.length === 0 && (
        <Box sx={{ ...cardSx, background: wf.surfaceAlt, color: wf.muted, fontSize: '12.5px' }}>
          This section is greyed out in the sidebar because your answers made it unnecessary.
        </Box>
      )}

      {saveErrorCount > 0 && (
        <Box sx={{ color: 'error.main', fontSize: '0.8rem' }}>
          {saveErrorCount} field{saveErrorCount === 1 ? '' : 's'} could not be saved. Review and
          retry the remaining changes.
        </Box>
      )}

      {!embedded && (
        <IntakePageFooter
          primaryLabel={nextLabel}
          onPrimary={validateThenContinue}
          onBack={onBack}
          saveLabel={saveMode === 'explicit' ? (isSaving ? 'Saving…' : 'Save changes') : undefined}
          onSave={validateThenSave}
          saveDisabled={
            isSaving || dirtyFieldCount === 0 || (shouldShowValidation && hasValidationErrors)
          }
          disabled={isSaving || saveErrorCount > 0 || Boolean(continueDisabledReason)}
          disabledReason={
            isSaving
              ? 'Wait for your changes to finish saving before continuing.'
              : saveErrorCount > 0
                ? 'Retry the changes that could not be saved before continuing.'
                : (continueDisabledReason ?? missingRequiredNote)
          }
        />
      )}
      {embedded && saveMode === 'explicit' && onSaveChanges && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            disabled={
              isSaving || dirtyFieldCount === 0 || (shouldShowValidation && hasValidationErrors)
            }
            onClick={validateThenSave}
          >
            {isSaving ? 'Saving…' : 'Save changes'}
          </Button>
        </Box>
      )}
      <HelpPanelDrawer
        drawerId={drawerId}
        panel={selectedGroup?.help_panel ?? null}
        open={Boolean(selectedGroup?.help_panel)}
        onClose={closeGroup}
        onAction={onHelpAction}
      />
    </>
  );
}
